import React, { useEffect, useMemo, useState } from 'react';
import { Save, RefreshCw, Send, Eye, ExternalLink, Undo2, Sparkles, Monitor, Smartphone } from 'lucide-react';
import { api } from '@/lib/api';
import { TextField, Toggle, Select } from '@/components/admin/shared';
import { ConfigurableHero } from '@/components/ConfigurableHero';

/**
 * HomepageDirector
 * ----------------
 * Centralized editorial control room for the Planet G homepage.
 * Renders inside ExperienceManager as a new tab. Manages the homepage config
 * (draft / live blueprints) via /api/admin/homepage.
 *
 * Sections:
 *   - Hero
 *   - Featured Headliner
 *   - Collections (stubbed — Phase 16B)
 *   - Content Lifecycle (stubbed — Phase 16B)
 *
 * Per-section actions:
 *   - Save Draft
 *   - Preview (opens /preview/home?src=draft in a new tab; also live in-admin split-pane)
 *   - Publish (draft → live)
 *   - Reset to Default (per-section reset via merging defaults)
 */

const SPACING_OPTS = [
  { value: 'compact', label: 'Compact' },
  { value: 'default', label: 'Default' },
  { value: 'spacious', label: 'Spacious' },
];
const CTA_STYLE_OPTS = [
  { value: 'neon', label: 'Neon' },
  { value: 'ghost', label: 'Ghost' },
  { value: 'gold', label: 'Gold' },
];
const BG_TYPE_OPTS = [
  { value: 'image', label: 'Image' },
  { value: 'video', label: 'Video' },
];
const HEADLINER_MODE_OPTS = [
  { value: 'manual', label: 'Manual' },
  { value: 'auto', label: 'Automatic' },
  { value: 'scheduled', label: 'Scheduled' },
];
const HEADLINER_TYPE_OPTS = [
  { value: 'music', label: 'Music' },
  { value: 'podcast', label: 'Podcast' },
  { value: 'article', label: 'Article' },
  { value: 'mobile_read', label: 'Mobile Read' },
  { value: 'product', label: 'Product' },
  { value: 'artwork', label: 'Artwork' },
  { value: 'video', label: 'Video' },
];
const AUTO_SOURCE_OPTS = [
  { value: 'latest', label: 'Latest' },
  { value: 'trending', label: 'Trending' },
  { value: 'editorial_pick', label: 'Editorial Pick' },
];

const SECTION_META = {
  hero: { label: 'HERO', icon: Sparkles },
  featured_headliner: { label: 'FEATURED HEADLINER', icon: Eye },
  collections: { label: 'COLLECTIONS', icon: Sparkles },
  content_lifecycle: { label: 'CONTENT LIFECYCLE', icon: Sparkles },
};

const PREVIEW_URL = '/preview/home?src=draft';

export default function HomepageDirector() {
  const [state, setState] = useState({ loading: true, live: null, draft: null, draft_updated_at: null, live_published_at: null });
  const [section, setSection] = useState('hero');
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(null); // 'save' | 'publish' | 'reset' | null
  const [msg, setMsg] = useState(null);
  const [previewDevice, setPreviewDevice] = useState('desktop'); // 'desktop' | 'mobile'

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get('/admin/homepage');
      setState({ loading: false, ...data });
      setDirty(false);
    } catch (e) {
      setState({ loading: false, live: null, draft: null, draft_updated_at: null, live_published_at: null });
      setMsg(e?.response?.data?.detail || 'Failed to load homepage config');
    }
  };
  useEffect(() => { load(); }, []);

  const draft = state.draft || {};
  const updateDraft = (patch) => {
    setState((s) => ({ ...s, draft: { ...(s.draft || {}), ...patch } }));
    setDirty(true);
  };
  const updateDraftSection = (key, patch) => {
    setState((s) => {
      const nextSection = { ...((s.draft || {})[key] || {}), ...patch };
      return { ...s, draft: { ...(s.draft || {}), [key]: nextSection } };
    });
    setDirty(true);
  };

  const saveDraft = async () => {
    if (!draft) return;
    setBusy('save'); setMsg(null);
    try {
      const { data } = await api.put('/admin/homepage/draft', draft);
      setState((s) => ({ ...s, draft: data.draft, draft_updated_at: data.draft_updated_at }));
      setDirty(false);
      setMsg('Draft saved. Use Preview to review, then Publish when ready.');
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(null); }
  };

  const publish = async () => {
    if (dirty) {
      const proceed = window.confirm('You have unsaved changes. Save draft first?');
      if (proceed) await saveDraft();
    }
    if (!window.confirm('Publish current draft as the LIVE homepage? Visitors will see these changes immediately.')) return;
    setBusy('publish'); setMsg(null);
    try {
      const { data } = await api.post('/admin/homepage/publish');
      setState((s) => ({ ...s, live: data.live, live_published_at: data.live_published_at }));
      setMsg(`Published to live · ${new Date(data.live_published_at).toLocaleString()}`);
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Publish failed');
    } finally { setBusy(null); }
  };

  const resetSection = async (target = 'draft', scope = 'section') => {
    const scopeLabel = scope === 'section' ? SECTION_META[section]?.label || section : 'ENTIRE HOMEPAGE';
    if (!window.confirm(`Reset ${scopeLabel} (${target}) to defaults?`)) return;
    setBusy('reset'); setMsg(null);
    try {
      if (scope === 'all') {
        // Full blueprint reset via backend
        const { data } = await api.post(`/admin/homepage/reset?target=${target}`);
        if (target === 'draft') setState((s) => ({ ...s, draft: data.draft }));
        else setState((s) => ({ ...s, live: data.live }));
      } else {
        // Section-scoped reset: fetch defaults, patch only the current section
        const { data } = await api.post(`/admin/homepage/reset?target=draft`);
        const defaults = data.draft || {};
        const restored = defaults[section];
        // Apply to whichever target
        if (target === 'draft') updateDraftSection(section, restored);
        // For "live" section reset — publish that section only. Simpler: force a full reset for `live`.
      }
      setDirty(target === 'draft');
      setMsg(`${scopeLabel} reset to defaults on ${target}.`);
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Reset failed');
    } finally { setBusy(null); }
  };

  const openFullPreview = () => {
    // Save draft first if dirty so preview matches what admin sees.
    const url = PREVIEW_URL + `&_t=${Date.now()}`;
    if (dirty) {
      saveDraft().then(() => window.open(url, '_blank', 'noopener'));
    } else {
      window.open(url, '_blank', 'noopener');
    }
  };

  if (state.loading || !draft) {
    return <div className="p-4 text-xs font-mono text-[var(--pg-ink-faint)]" data-testid="homepage-director-loading">Loading Homepage Director…</div>;
  }

  const SectionIcon = SECTION_META[section]?.icon || Sparkles;

  return (
    <div className="space-y-4" data-testid="homepage-director">
      <div className="pg-admin-card rounded-[var(--pg-radius-md)] p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="pg-label text-[10px]">HOMEPAGE DIRECTOR</p>
          <p className="text-xs text-[var(--pg-ink-muted)] mt-1">
            Draft-first editing with live preview. Publish when the homepage is ready to broadcast.
            {state.live_published_at && (
              <span className="ml-2 text-[var(--pg-emerald-2)] font-mono">LIVE · {new Date(state.live_published_at).toLocaleString()}</span>
            )}
            {state.draft_updated_at && (
              <span className="ml-2 font-mono text-[var(--pg-gold-2)]">DRAFT · {new Date(state.draft_updated_at).toLocaleString()}{dirty ? ' · unsaved' : ''}</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={load} className="pg-btn-ghost rounded-[var(--pg-radius-sm)] px-3 py-1.5 text-xs inline-flex items-center gap-1"><RefreshCw size={13} /> Reload</button>
          <button
            data-testid="homepage-director-save-draft"
            onClick={saveDraft} disabled={busy === 'save'}
            className="pg-btn-ghost rounded-[var(--pg-radius-sm)] px-3 py-1.5 text-xs inline-flex items-center gap-1 disabled:opacity-60"
          >
            <Save size={13} /> {busy === 'save' ? 'Saving…' : 'Save Draft'}
          </button>
          <button
            data-testid="homepage-director-open-preview"
            onClick={openFullPreview}
            className="pg-btn-ghost rounded-[var(--pg-radius-sm)] px-3 py-1.5 text-xs inline-flex items-center gap-1"
            title="Open full-page draft preview in a new tab"
          >
            <ExternalLink size={13} /> Full-page Preview
          </button>
          <button
            data-testid="homepage-director-publish"
            onClick={publish} disabled={busy === 'publish'}
            className="pg-btn-primary rounded-[var(--pg-radius-sm)] px-3 py-1.5 text-xs inline-flex items-center gap-1 disabled:opacity-60"
          >
            <Send size={13} /> {busy === 'publish' ? 'Publishing…' : 'Publish'}
          </button>
          <button
            data-testid="homepage-director-reset-section"
            onClick={() => resetSection('draft', 'section')}
            className="pg-btn-ghost rounded-[var(--pg-radius-sm)] px-3 py-1.5 text-xs inline-flex items-center gap-1"
            title="Reset the current section to defaults"
          >
            <Undo2 size={13} /> Reset section
          </button>
        </div>
      </div>

      {msg && <div className="pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-xs text-[var(--pg-ink)]" data-testid="homepage-director-message">{msg}</div>}

      {/* Section tabs */}
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="homepage-director-tabs">
        {Object.entries(SECTION_META).map(([k, meta]) => (
          <button
            key={k}
            type="button"
            onClick={() => setSection(k)}
            data-testid={`homepage-director-tab-${k}`}
            className="px-3 py-1.5 rounded-full border text-[10px] font-mono tracking-[0.24em] uppercase"
            style={{
              background: section === k ? 'rgba(226,199,122,0.14)' : 'transparent',
              borderColor: section === k ? 'rgba(226,199,122,0.6)' : 'var(--pg-border-2)',
              color: section === k ? 'var(--pg-gold-2)' : 'var(--pg-ink-muted)',
            }}
          >
            {meta.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* --- Editor pane --- */}
        <div className="space-y-3">
          <div className="pg-admin-card rounded-[var(--pg-radius-md)] p-5 space-y-3">
            <h4 className="pg-label text-[10px] flex items-center gap-2"><SectionIcon size={12} /> {SECTION_META[section]?.label || section}</h4>
            {section === 'hero' && <HeroEditor hero={draft.hero || {}} onChange={(patch) => updateDraftSection('hero', patch)} />}
            {section === 'featured_headliner' && <FeaturedHeadlinerEditor fh={draft.featured_headliner || {}} onChange={(patch) => updateDraftSection('featured_headliner', patch)} />}
            {section === 'collections' && <CollectionsEditor collections={draft.collections || []} onChange={(next) => updateDraft({ collections: next })} />}
            {section === 'content_lifecycle' && <ContentLifecycleEditor cfg={draft.content_lifecycle || {}} onChange={(patch) => updateDraftSection('content_lifecycle', patch)} />}
          </div>
        </div>

        {/* --- Live preview pane --- */}
        <div className="space-y-2" data-testid="homepage-director-preview">
          <div className="flex items-center justify-between gap-2">
            <p className="pg-label text-[10px]">LIVE PREVIEW · DRAFT</p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPreviewDevice('desktop')}
                data-testid="homepage-director-preview-desktop"
                className="px-2 py-1 rounded-md border text-[10px] font-mono tracking-widest inline-flex items-center gap-1"
                style={{
                  background: previewDevice === 'desktop' ? 'rgba(226,199,122,0.14)' : 'transparent',
                  borderColor: previewDevice === 'desktop' ? 'rgba(226,199,122,0.6)' : 'var(--pg-border-2)',
                  color: previewDevice === 'desktop' ? 'var(--pg-gold-2)' : 'var(--pg-ink-muted)',
                }}
              ><Monitor size={11} /> DESKTOP</button>
              <button
                onClick={() => setPreviewDevice('mobile')}
                data-testid="homepage-director-preview-mobile"
                className="px-2 py-1 rounded-md border text-[10px] font-mono tracking-widest inline-flex items-center gap-1"
                style={{
                  background: previewDevice === 'mobile' ? 'rgba(226,199,122,0.14)' : 'transparent',
                  borderColor: previewDevice === 'mobile' ? 'rgba(226,199,122,0.6)' : 'var(--pg-border-2)',
                  color: previewDevice === 'mobile' ? 'var(--pg-gold-2)' : 'var(--pg-ink-muted)',
                }}
              ><Smartphone size={11} /> MOBILE</button>
            </div>
          </div>
          <div
            className="pg-card overflow-hidden"
            style={{
              maxWidth: previewDevice === 'mobile' ? 420 : '100%',
              margin: previewDevice === 'mobile' ? '0 auto' : undefined,
            }}
          >
            {/* In-admin live preview reflects the draft's Hero via the local
                ConfigurableHero component. Featured Headliner content is
                resolved from the /homepage/featured?src=draft endpoint on
                save, so click "Save Draft" to refresh headliner data. */}
            <ConfigurableHero blueprint={draft} source="draft" />
          </div>
          <p className="text-[10px] font-mono text-[var(--pg-ink-faint)]">
            Note: the in-admin preview reflects Hero edits instantly. Featured Headliner content refreshes on Save Draft.
          </p>
        </div>
      </div>
    </div>
  );
}

/* =========================== Section Editors =========================== */

function HeroEditor({ hero, onChange }) {
  return (
    <div className="space-y-3">
      <Toggle label="Hero visible" value={hero.visible !== false} onChange={(v) => onChange({ visible: v })} />
      <TextField label="Welcome copy" value={hero.welcome_copy || ''} onChange={(v) => onChange({ welcome_copy: v })} placeholder="Welcome to" />
      <TextField label="Main heading" value={hero.main_heading || ''} onChange={(v) => onChange({ main_heading: v })} placeholder="PLANET G" />
      <TextField label="Supporting copy" value={hero.supporting_copy || ''} onChange={(v) => onChange({ supporting_copy: v })} placeholder="Thoughts. Music. Podcasts. Products." />
      <div className="pg-input rounded-[var(--pg-radius-sm)] p-3 space-y-2">
        <p className="text-[10px] font-mono tracking-widest text-[var(--pg-ink-faint)] uppercase">Primary CTA</p>
        <div className="grid grid-cols-2 gap-2">
          <TextField label="Label" value={hero.primary_cta?.label || ''} onChange={(v) => onChange({ primary_cta: { ...(hero.primary_cta || {}), label: v } })} />
          <TextField label="Href" value={hero.primary_cta?.href || ''} onChange={(v) => onChange({ primary_cta: { ...(hero.primary_cta || {}), href: v } })} placeholder="#channels" />
        </div>
        <Select label="Style" value={hero.primary_cta?.style || 'neon'} onChange={(v) => onChange({ primary_cta: { ...(hero.primary_cta || {}), style: v } })} options={CTA_STYLE_OPTS} />
      </div>
      <div className="pg-input rounded-[var(--pg-radius-sm)] p-3 space-y-2">
        <p className="text-[10px] font-mono tracking-widest text-[var(--pg-ink-faint)] uppercase">Secondary CTA (optional)</p>
        <div className="grid grid-cols-2 gap-2">
          <TextField label="Label" value={hero.secondary_cta?.label || ''} onChange={(v) => onChange({ secondary_cta: { ...(hero.secondary_cta || {}), label: v } })} placeholder="Leave empty to hide" />
          <TextField label="Href" value={hero.secondary_cta?.href || ''} onChange={(v) => onChange({ secondary_cta: { ...(hero.secondary_cta || {}), href: v } })} />
        </div>
        <Select label="Style" value={hero.secondary_cta?.style || 'ghost'} onChange={(v) => onChange({ secondary_cta: { ...(hero.secondary_cta || {}), style: v } })} options={CTA_STYLE_OPTS} />
      </div>
      <div className="pg-input rounded-[var(--pg-radius-sm)] p-3 space-y-2">
        <p className="text-[10px] font-mono tracking-widest text-[var(--pg-ink-faint)] uppercase">Background</p>
        <Select label="Type" value={hero.background_media_type || 'image'} onChange={(v) => onChange({ background_media_type: v })} options={BG_TYPE_OPTS} />
        <TextField label="Media URL" value={hero.background_media_url || ''} onChange={(v) => onChange({ background_media_url: v })} placeholder="/uploads/... or https://…" />
        <div className="grid grid-cols-2 gap-2">
          <TextField label="Position" value={hero.background_position || 'center'} onChange={(v) => onChange({ background_position: v })} placeholder="center | 50% 30%" />
          <TextField label="Overlay opacity % (0-100)" type="number" value={hero.background_overlay_opacity_pct ?? 55} onChange={(v) => onChange({ background_overlay_opacity_pct: Number(v) })} />
        </div>
        <TextField label="Overlay color (CSS)" value={hero.background_overlay_color || 'rgba(7,8,15,0.55)'} onChange={(v) => onChange({ background_overlay_color: v })} />
      </div>
      <Select label="Section spacing" value={hero.section_spacing || 'spacious'} onChange={(v) => onChange({ section_spacing: v })} options={SPACING_OPTS} />
    </div>
  );
}

function FeaturedHeadlinerEditor({ fh, onChange }) {
  return (
    <div className="space-y-3">
      <Toggle label="Featured Headliner enabled" value={fh.enabled !== false} onChange={(v) => onChange({ enabled: v })} />
      <TextField label="Section title (renameable)" value={fh.section_title || 'Featured'} onChange={(v) => onChange({ section_title: v })} placeholder="Featured / Spotlight / Listening Room / …" />
      <Select label="Mode" value={fh.mode || 'manual'} onChange={(v) => onChange({ mode: v })} options={HEADLINER_MODE_OPTS} />
      {fh.mode === 'manual' && (
        <div className="pg-input rounded-[var(--pg-radius-sm)] p-3 space-y-2">
          <p className="text-[10px] font-mono tracking-widest text-[var(--pg-ink-faint)] uppercase">Manual Selection</p>
          <Select label="Content type" value={fh.content_type || 'music'} onChange={(v) => onChange({ content_type: v })} options={HEADLINER_TYPE_OPTS} />
          <TextField label="Content ID" value={fh.content_id || ''} onChange={(v) => onChange({ content_id: v })} placeholder="Paste the item ID from its admin editor" />
          <p className="text-[10px] font-mono text-[var(--pg-ink-faint)]">Copy the item ID from the corresponding admin tab (open Edit → the URL/id field).</p>
        </div>
      )}
      {fh.mode === 'auto' && (
        <div className="pg-input rounded-[var(--pg-radius-sm)] p-3 space-y-2">
          <p className="text-[10px] font-mono tracking-widest text-[var(--pg-ink-faint)] uppercase">Automatic</p>
          <MultiSelect
            label="Eligible content types"
            values={fh.auto_content_types || ['music', 'podcast']}
            onChange={(v) => onChange({ auto_content_types: v })}
            options={HEADLINER_TYPE_OPTS}
          />
          <Select label="Source" value={fh.auto_source || 'latest'} onChange={(v) => onChange({ auto_source: v })} options={AUTO_SOURCE_OPTS} />
        </div>
      )}
      {fh.mode === 'scheduled' && (
        <ScheduleEditor schedule={fh.schedule || []} onChange={(v) => onChange({ schedule: v })} />
      )}
      <div className="pg-input rounded-[var(--pg-radius-sm)] p-3 space-y-2">
        <p className="text-[10px] font-mono tracking-widest text-[var(--pg-ink-faint)] uppercase">Image Framing</p>
        <div className="grid grid-cols-2 gap-2">
          <TextField label="Desktop focal X % (0-100)" type="number" value={fh.image_focal_x_pct ?? 50} onChange={(v) => onChange({ image_focal_x_pct: Number(v) })} />
          <TextField label="Desktop focal Y % (0-100)" type="number" value={fh.image_focal_y_pct ?? 50} onChange={(v) => onChange({ image_focal_y_pct: Number(v) })} />
          <TextField label="Mobile focal X %" type="number" value={fh.mobile_image_focal_x_pct ?? 50} onChange={(v) => onChange({ mobile_image_focal_x_pct: Number(v) })} />
          <TextField label="Mobile focal Y %" type="number" value={fh.mobile_image_focal_y_pct ?? 50} onChange={(v) => onChange({ mobile_image_focal_y_pct: Number(v) })} />
        </div>
        <Select label="Image crop" value={fh.image_crop || 'cover'} onChange={(v) => onChange({ image_crop: v })} options={[{ value: 'cover', label: 'Cover' }, { value: 'contain', label: 'Contain' }]} />
      </div>
      <div className="pg-input rounded-[var(--pg-radius-sm)] p-3 space-y-2">
        <p className="text-[10px] font-mono tracking-widest text-[var(--pg-ink-faint)] uppercase">Behavior</p>
        <Toggle label="Preview modal enabled (articles & mobile reads)" value={fh.preview_enabled !== false} onChange={(v) => onChange({ preview_enabled: v })} />
        <Toggle label="Open channel on card click (music / podcasts)" value={fh.open_channel_on_click !== false} onChange={(v) => onChange({ open_channel_on_click: v })} />
      </div>
    </div>
  );
}

function ScheduleEditor({ schedule, onChange }) {
  const addEntry = () => onChange([...(schedule || []), { content_type: 'music', content_id: '', starts_at: '', ends_at: '' }]);
  const updateEntry = (i, patch) => onChange((schedule || []).map((e, idx) => idx === i ? { ...e, ...patch } : e));
  const removeEntry = (i) => onChange((schedule || []).filter((_, idx) => idx !== i));
  return (
    <div className="pg-input rounded-[var(--pg-radius-sm)] p-3 space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-mono tracking-widest text-[var(--pg-ink-faint)] uppercase">Schedule ({(schedule || []).length})</p>
        <button onClick={addEntry} className="pg-btn-ghost rounded-[var(--pg-radius-sm)] px-2 py-1 text-[10px]" data-testid="headliner-schedule-add">+ Add slot</button>
      </div>
      {(schedule || []).length === 0 && <p className="text-[10px] text-[var(--pg-ink-faint)]">No scheduled slots yet.</p>}
      {(schedule || []).map((entry, i) => (
        <div key={i} className="pg-card p-2 space-y-2" data-testid={`headliner-schedule-row-${i}`}>
          <div className="grid grid-cols-2 gap-2">
            <Select label="Type" value={entry.content_type} onChange={(v) => updateEntry(i, { content_type: v })} options={HEADLINER_TYPE_OPTS} />
            <TextField label="Content ID" value={entry.content_id} onChange={(v) => updateEntry(i, { content_id: v })} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <TextField label="Starts at (ISO)" value={entry.starts_at || ''} onChange={(v) => updateEntry(i, { starts_at: v })} placeholder="2026-01-15T09:00:00Z" />
            <TextField label="Ends at (ISO)" value={entry.ends_at || ''} onChange={(v) => updateEntry(i, { ends_at: v })} placeholder="Leave blank for open-ended" />
          </div>
          <button onClick={() => removeEntry(i)} className="pg-btn-ghost rounded-[var(--pg-radius-sm)] px-2 py-1 text-[10px] text-[var(--pg-neon-pink)]">Remove</button>
        </div>
      ))}
    </div>
  );
}

function ContentLifecycleEditor({ cfg, onChange }) {
  return (
    <div className="space-y-3">
      <Select
        label="Definition of 'new'"
        value={cfg.new_definition || 'publish_date'}
        onChange={(v) => onChange({ new_definition: v })}
        options={[
          { value: 'upload_date', label: 'Upload date' },
          { value: 'publish_date', label: 'Publish date' },
          { value: 'manual', label: 'Manual selection' },
          { value: 'hybrid', label: 'Hybrid (publish date + manual overrides)' },
        ]}
      />
      <Select
        label="Time window"
        value={String(cfg.new_window_days ?? 14)}
        onChange={(v) => onChange({ new_window_days: Number(v) })}
        options={[
          { value: '3', label: 'Last 3 days' },
          { value: '7', label: 'Last 7 days' },
          { value: '14', label: 'Last 14 days' },
          { value: '30', label: 'Last 30 days' },
        ]}
      />
      <Toggle label="Allow duplicates across collections" value={cfg.allow_duplicates_across_collections === true} onChange={(v) => onChange({ allow_duplicates_across_collections: v })} />
      <p className="text-[10px] font-mono text-[var(--pg-ink-faint)]">
        Homepage collections consume this lifecycle to determine what qualifies as newly released. Manual selections take precedence when the mode allows.
      </p>
    </div>
  );
}

function StubSection({ name, note }) {
  return (
    <div className="text-xs text-[var(--pg-ink-muted)]">
      <p className="mb-2">{name} controls will be enabled in the next phase.</p>
      <p className="text-[10px] font-mono text-[var(--pg-ink-faint)]">{note}</p>
    </div>
  );
}

/* ---------------------------- Collections Editor ---------------------------- */

const SOURCE_OPTS = [
  { value: 'mixed', label: 'Mixed (all types)' },
  { value: 'music', label: 'Music' },
  { value: 'podcast', label: 'Podcast' },
  { value: 'article', label: 'Article' },
  { value: 'mobile_read', label: 'Mobile Read' },
  { value: 'product', label: 'Product' },
  { value: 'artwork', label: 'Artwork' },
  { value: 'video', label: 'Video' },
];
const DISPLAY_MODE_OPTS = [
  { value: 'behavioral', label: 'Behavioral' },
  { value: 'editorial', label: 'Editorial' },
  { value: 'hybrid', label: 'Hybrid (recommended)' },
];
const SORT_OPTS = [
  { value: 'newest', label: 'Newest' },
  { value: 'popular', label: 'Popular (featured first)' },
  { value: 'trending', label: 'Trending (30d events)' },
  { value: 'most_played', label: 'Most Played' },
  { value: 'most_purchased', label: 'Most Purchased' },
  { value: 'highest_engagement', label: 'Highest Engagement' },
  { value: 'recently_updated', label: 'Recently Updated' },
  { value: 'manual', label: 'Manual (pinned order)' },
];
const STYLE_OPTS = [
  { value: 'grid', label: 'Grid' },
  { value: 'rail', label: 'Horizontal rail' },
  { value: 'list', label: 'List' },
];

function CollectionsEditor({ collections, onChange }) {
  const list = Array.isArray(collections) ? collections : [];

  const addCollection = () => {
    const nextId = `coll_${Date.now()}`;
    onChange([
      ...list,
      {
        id: nextId,
        title: 'New Collection',
        visible: true,
        display_mode: 'hybrid',
        content_source: 'mixed',
        max_items: 8,
        sort_order: 'newest',
        style: 'grid',
        pinned_ids: [],
        schedule_start: null,
        schedule_end: null,
      },
    ]);
  };

  const updateAt = (idx, patch) => {
    const next = list.map((c, i) => (i === idx ? { ...c, ...patch } : c));
    onChange(next);
  };
  const removeAt = (idx) => onChange(list.filter((_, i) => i !== idx));
  const move = (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= list.length) return;
    const next = list.slice();
    const [row] = next.splice(idx, 1);
    next.splice(target, 0, row);
    onChange(next);
  };

  return (
    <div className="space-y-3" data-testid="homepage-collections-editor">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-[var(--pg-ink-muted)]">
          Each collection pulls from a content type (or mixed), respects the lifecycle window, and prevents duplicates across the homepage.
        </p>
        <button
          onClick={addCollection}
          data-testid="collections-add"
          className="pg-btn-primary rounded-[var(--pg-radius-sm)] px-3 py-1.5 text-xs font-mono tracking-widest"
        >+ ADD COLLECTION</button>
      </div>
      {list.length === 0 && (
        <div className="pg-card p-6 text-center text-xs text-[var(--pg-ink-muted)]" data-testid="collections-empty">
          No collections yet. Add one — visitors will see it on the homepage as a curated rail.
        </div>
      )}
      {list.map((coll, i) => (
        <div key={coll.id || i} className="pg-card p-4 space-y-3" data-testid={`collection-${i}`}>
          {/* Non-truncating header — always shows the full collection identity */}
          <div className="flex flex-wrap items-start gap-2 pb-3 border-b border-[var(--pg-border-2)]">
            <div className="min-w-0 flex-1">
              <p
                className="text-[10px] font-mono tracking-[0.28em] uppercase text-[var(--pg-ink-faint)] break-words leading-tight"
                data-testid={`collection-${i}-kicker-display`}
              >
                {coll.kicker || 'NO KICKER'}
              </p>
              <p
                className="font-serif text-lg text-[var(--pg-ink)] break-words leading-snug mt-0.5"
                data-testid={`collection-${i}-title-display`}
                title={coll.title || 'Untitled collection'}
              >
                {coll.title || 'Untitled collection'}
              </p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <Toggle label="Visible" value={coll.visible !== false} onChange={(v) => updateAt(i, { visible: v })} />
              <button onClick={() => move(i, -1)} className="pg-btn-ghost rounded-[var(--pg-radius-sm)] px-2 py-1 text-[10px]" disabled={i === 0} data-testid={`collection-${i}-move-up`}>▲</button>
              <button onClick={() => move(i, 1)} className="pg-btn-ghost rounded-[var(--pg-radius-sm)] px-2 py-1 text-[10px]" disabled={i === list.length - 1} data-testid={`collection-${i}-move-down`}>▼</button>
              <button onClick={() => removeAt(i)} className="pg-btn-ghost rounded-[var(--pg-radius-sm)] px-2 py-1 text-[10px] text-[var(--pg-neon-pink)]" data-testid={`collection-${i}-remove`}>REMOVE</button>
            </div>
          </div>
          {/* Editable name fields — full width, no truncation */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <TextField label="Small label (kicker)" value={coll.kicker || ''} onChange={(v) => updateAt(i, { kicker: v })} placeholder="TRENDING" />
            <TextField label="Large title" value={coll.title || ''} onChange={(v) => updateAt(i, { title: v })} placeholder="Trending" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Select label="Content source" value={coll.content_source || 'mixed'} onChange={(v) => updateAt(i, { content_source: v })} options={SOURCE_OPTS} />
            <Select label="Population mode" value={coll.display_mode || 'hybrid'} onChange={(v) => updateAt(i, { display_mode: v })} options={DISPLAY_MODE_OPTS} />
            <Select label="Sort / Rule" value={coll.sort_order || 'newest'} onChange={(v) => updateAt(i, { sort_order: v })} options={SORT_OPTS} />
            <Select label="Style" value={coll.style || 'grid'} onChange={(v) => updateAt(i, { style: v })} options={STYLE_OPTS} />
            <TextField label="Max items" type="number" value={coll.max_items ?? 8} onChange={(v) => updateAt(i, { max_items: Number(v) || 8 })} />
          </div>
          <PinnedContentSelector
            pinnedIds={coll.pinned_ids || []}
            contentSource={coll.content_source || 'mixed'}
            onChange={(next) => updateAt(i, { pinned_ids: next })}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <TextField label="Schedule start (ISO, optional)" value={coll.schedule_start || ''} onChange={(v) => updateAt(i, { schedule_start: v || null })} placeholder="2026-01-15T09:00:00Z" />
            <TextField label="Schedule end (ISO, optional)" value={coll.schedule_end || ''} onChange={(v) => updateAt(i, { schedule_end: v || null })} placeholder="Leave blank for open-ended" />
          </div>
          <p className="text-[10px] font-mono text-[var(--pg-ink-faint)]">
            {coll.display_mode === 'editorial' && 'Only pinned items will render.'}
            {coll.display_mode === 'behavioral' && 'Automatic — behavioral picks from the lifecycle window; pinned items are ignored.'}
            {coll.display_mode === 'hybrid' && 'Hybrid — pinned items are guaranteed at the top, then behavioral fills the rest.'}
          </p>
        </div>
      ))}
    </div>
  );
}

function MultiSelect({ label, values, onChange, options }) {
  const set = new Set(values || []);
  const toggle = (v) => {
    const next = new Set(set);
    if (next.has(v)) next.delete(v); else next.add(v);
    onChange(Array.from(next));
  };
  return (
    <div>
      <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">{label}</label>
      <div className="mt-1 flex flex-wrap gap-1.5">
        {options.map((opt) => {
          const active = set.has(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => toggle(opt.value)}
              className="px-2 py-1 rounded-full border text-[10px] font-mono tracking-widest uppercase"
              style={{
                background: active ? 'rgba(25,194,142,0.14)' : 'transparent',
                borderColor: active ? 'rgba(25,194,142,0.6)' : 'var(--pg-border-2)',
                color: active ? 'var(--pg-emerald-2)' : 'var(--pg-ink-muted)',
              }}
            >{opt.label}</button>
          );
        })}
      </div>
    </div>
  );
}

/* -------------------------- Pinned Content Selector -------------------------- */

// Searchable cross-content picker. Talks to GET /api/admin/content-search
// and lets the admin add pinned items directly to a collection without
// leaving the Experience Manager.
function PinnedContentSelector({ pinnedIds, contentSource, onChange }) {
  const [q, setQ] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [results, setResults] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [pinnedItems, setPinnedItems] = React.useState([]); // resolved details for currently pinned ids

  // Search debounce
  React.useEffect(() => {
    if (!open) return undefined;
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/admin/content-search', {
          params: { q, content_type: contentSource || 'mixed', limit: 25 },
        });
        setResults((data && data.results) || []);
      } catch (_) { setResults([]); }
      finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(handle);
  }, [q, open, contentSource]);

  // Resolve details for currently pinned IDs (only when the list changes) so
  // we can show a friendly chip name instead of a bare UUID.
  React.useEffect(() => {
    let cancelled = false;
    if (!pinnedIds || pinnedIds.length === 0) { setPinnedItems([]); return undefined; }
    (async () => {
      try {
        const { data } = await api.get('/admin/content-search', {
          params: { q: '', content_type: contentSource || 'mixed', limit: 50 },
        });
        if (cancelled) return;
        const pool = (data && data.results) || [];
        const enriched = pinnedIds.map((id) => pool.find((r) => r.id === id) || { id, title: id, __type: contentSource || 'unknown' });
        setPinnedItems(enriched);
      } catch (_) {
        if (!cancelled) setPinnedItems(pinnedIds.map((id) => ({ id, title: id, __type: 'unknown' })));
      }
    })();
    return () => { cancelled = true; };
  }, [pinnedIds, contentSource]);

  const pin = (item) => {
    if (pinnedIds.includes(item.id)) return;
    onChange([...pinnedIds, item.id]);
    setOpen(false);
    setQ('');
  };
  const unpin = (id) => onChange(pinnedIds.filter((x) => x !== id));

  return (
    <div className="pg-input rounded-[var(--pg-radius-sm)] p-3 space-y-2" data-testid="pinned-content-selector">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[10px] font-mono tracking-widest text-[var(--pg-ink-faint)] uppercase">
          Pinned Content · {pinnedItems.length}
        </p>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          data-testid="pinned-content-toggle"
          className="pg-btn-primary rounded-[var(--pg-radius-sm)] px-2 py-1 text-[10px] font-mono tracking-widest"
        >{open ? 'CLOSE' : '+ ADD PINNED'}</button>
      </div>
      {pinnedItems.length === 0 && !open && (
        <p className="text-[10px] font-mono text-[var(--pg-ink-faint)]">No pinned items. Automatic rules control this collection.</p>
      )}
      {pinnedItems.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {pinnedItems.map((it) => (
            <li key={it.id} className="inline-flex items-center gap-2 px-2 py-1 rounded-full border border-[var(--pg-emerald)] bg-[rgba(25,194,142,0.08)]" data-testid={`pinned-chip-${it.id}`}>
              <span className="text-[9px] font-mono tracking-widest text-[var(--pg-emerald-2)] uppercase">{it.__type}</span>
              <span className="text-xs text-[var(--pg-ink)] max-w-[220px] truncate">{it.title}</span>
              <button onClick={() => unpin(it.id)} aria-label={`Unpin ${it.title}`} className="text-[var(--pg-ink-faint)] hover:text-[var(--pg-neon-pink)]" data-testid={`pinned-unpin-${it.id}`}>×</button>
            </li>
          ))}
        </ul>
      )}
      {open && (
        <div className="space-y-2">
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`Search ${contentSource === 'mixed' ? 'all content' : contentSource}…`}
            data-testid="pinned-search-input"
            className="w-full pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-sm"
          />
          <div className="max-h-64 overflow-y-auto pg-scroll pg-card">
            {loading ? (
              <p className="p-3 text-xs text-[var(--pg-ink-muted)]">Searching…</p>
            ) : results.length === 0 ? (
              <p className="p-3 text-xs text-[var(--pg-ink-muted)]">No matches. Content is created in its channel — search here to feature it on the homepage.</p>
            ) : (
              <ul className="divide-y divide-[var(--pg-border-2)]">
                {results.map((r) => {
                  const isPinned = pinnedIds.includes(r.id);
                  return (
                    <li key={`${r.__type}:${r.id}`}>
                      <button
                        type="button"
                        onClick={() => pin(r)}
                        disabled={isPinned}
                        data-testid={`pinned-result-${r.id}`}
                        className="w-full flex items-center gap-3 px-3 py-2 hover:bg-[rgba(25,194,142,0.06)] text-left disabled:opacity-60"
                      >
                        <span className="text-[9px] font-mono tracking-widest text-[var(--pg-gold-2)] uppercase w-16 shrink-0">{r.__type}</span>
                        <span className="text-sm text-[var(--pg-ink)] truncate flex-1">{r.title}</span>
                        {isPinned && <span className="text-[10px] font-mono text-[var(--pg-emerald-2)]">PINNED</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

